import React from 'react';
import { StatsigProvider } from '@statsig/react-bindings';
import { useAuth } from '../context/AuthContext';
import { statsigConfig, getDeviceInfo } from '../config/statsig';
import { mapSupabaseUserToStatsig } from '../utils/statsigUser';
import { createLogger } from '../utils/logger-ssr-safe';
import { initStatsigSentryIntegration } from '../utils/sentryStatsigIntegration';
import { useConvexProfile } from '../hooks/convex/useConvexProfile';
import type { User } from '../types/auth';

const logger = createLogger({ module: 'StatsigWrapper', level: 'info' });

interface StatsigWrapperProps {
  children: React.ReactNode;
}

// Define which routes are protected and should always have an authenticated user
const PROTECTED_ROUTES = [
  '/home',
  '/profile',
  '/settings',
  '/files',
  '/resumes',
  '/job-search',
  '/timeline',
  '/retrospectives',
  '/admin'
];

// Check if current route is protected
const isProtectedRoute = (pathname: string): boolean => {
  return PROTECTED_ROUTES.some(route => pathname.startsWith(route));
};

export const StatsigWrapper: React.FC<StatsigWrapperProps> = ({
  children
}) => {
  const { user, loading } = useAuth();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

  // Use Convex for real-time profile data (replaces REST API call)
  const { profile: convexProfile } = useConvexProfile();

  // Get device info for user mapping
  const deviceInfo = React.useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    return getDeviceInfo();
  }, []);

  // Transform Convex profile to the expected format for Statsig
  const userProfile = React.useMemo(() => {
    if (!convexProfile) return null;
    // Transform camelCase to snake_case for backward compatibility with mapSupabaseUserToStatsig
    return {
      user_type: convexProfile.userType,
      job_title: convexProfile.jobTitle,
      company: convexProfile.company,
      ...convexProfile,
    } as Record<string, unknown>;
  }, [convexProfile]);

  React.useEffect(() => {
    if (user?.id && userProfile) {
      logger.debug('Fetched user profile for Statsig via Convex', {
        userId: user.id.substring(0, 8) + '...',
        userType: userProfile.user_type
      });
    }
  }, [user?.id, userProfile]);

  // Map Supabase user to Statsig user format with database profile
  const statsigUser = React.useMemo(() => {
    return mapSupabaseUserToStatsig(user as User | null, userProfile ?? undefined, deviceInfo);
  }, [user, userProfile, deviceInfo]);

  // Debug logging when user changes
  React.useEffect(() => {
    const isProtected = isProtectedRoute(pathname);
    logger.debug('User state changed', {
      hasUser: !!user,
      statsigUserID: statsigUser.userID,
      isAnonymous: statsigUser.userID === 'anonymous',
      userEmail: user?.email?.substring(0, 3) + '***' || 'none',
      isProtectedRoute: isProtected,
      authLoading: loading
    });
  }, [user, loading, statsigUser.userID, pathname]);

  // IMPORTANT: Initialize Sentry integration after Statsig is ready
  // This hook must be called BEFORE any early returns
  React.useEffect(() => {
    // Small delay to ensure Statsig is initialized
    const timer = setTimeout(() => {
      try {
        initStatsigSentryIntegration();
        logger.debug('Statsig-Sentry integration initialized');
      } catch (error) {
        logger.error('Failed to initialize Statsig-Sentry integration:', error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Check if we have a real Statsig key
  const hasRealKey = !!statsigConfig.clientKey;

  // If no client key, use a placeholder with network completely disabled
  const sdkKey = statsigConfig.clientKey || 'client-placeholder-no-network';

  if (!hasRealKey) {
    logger.debug('Statsig client key not found. Running in offline mode with defaults.');
  } else {
    // PERFORMANCE FIX: Always initialize immediately, never wait for auth
    logger.debug('Initializing Statsig immediately for optimal performance', {
      userID: statsigUser.userID,
      isAnonymous: statsigUser.userID === 'anonymous',
      route: pathname,
      isProtectedRoute: isProtectedRoute(pathname)
    });
  }

  return (
    <StatsigProvider
      sdkKey={sdkKey}
      user={statsigUser}
      options={{
        ...statsigConfig.options,
        // Ensure proper environment detection
        environment: {
          tier: import.meta.env.MODE
        },
        // PERFORMANCE: Enable local storage caching for faster subsequent loads
        disableStorage: false,
        // When no real key, completely disable all network traffic to prevent 401 errors
        ...(hasRealKey ? {} : {
          networkConfig: {
            preventAllNetworkTraffic: true,
          },
        }),
      }}
      // TIMING FIX: Show loading component while Statsig initializes
      // This prevents child components from trying to use Statsig hooks before client is ready
      // The loading state is brief (typically <100ms) and prevents console warnings
      loadingComponent={null}
    >
      {children}
    </StatsigProvider>
  );
}; 